import { useMemo, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { animeList, animeCategories } from '@/mocks/anime';

const breadcrumbMap: Record<string, string> = {
  all: '全部动漫',
  riman: '日漫',
  guoman: '国漫',
  jingdian: '经典番剧',
  rexue: '热血',
  lianai: '恋爱',
  kehuan: '科幻',
  xuanhuan: '玄幻',
};

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const currentCategory = animeCategories.find((c) => c.slug === slug);

  const filteredList = useMemo(() => {
    let list = slug === 'all' ? animeList : animeList.filter((a) => a.category === slug);
    switch (sortBy) {
      case 'rating':
        return [...list].sort((a, b) => b.rating - a.rating);
      case 'views':
        return [...list].sort((a, b) => b.views - a.views);
      default:
        return [...list].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    }
  }, [slug, sortBy]);

  const categoryName = breadcrumbMap[slug] || slug;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 6080影视`,
    description: `6080影视${categoryName}专区，提供高清免费在线观看，不缓冲流畅追番体验。`,
    url: `https://6080ys.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '6080影视',
      url: 'https://6080ys.com',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://6080ys.com/' },
        { '@type': 'ListItem', position: 2, name: categoryName },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <header>
        <Navbar />
      </header>

      <main>
        <div className="pt-20 pb-8 bg-background-900/50">
          <div className="container-custom">
            <nav className="flex items-center gap-2 text-xs md:text-sm mb-6" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-500 hover:text-primary-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <span className="text-foreground-300">{categoryName}</span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
              {categoryName}
            </h1>
            <p className="text-foreground-500 text-sm">
              共 {filteredList.length} 部动漫 · 高清免费在线观看
            </p>
          </div>
        </div>

        <section className="py-6">
          <div className="container-custom">
            <div className="flex flex-col lg:flex-row gap-6">
              <aside className="lg:w-48 flex-shrink-0">
                <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0">
                  {animeCategories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => {
                        navigate(`/category/${cat.slug}`);
                      }}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        slug === cat.slug
                          ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                          : 'text-foreground-400 hover:text-foreground-200 hover:bg-background-800 border border-transparent'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                  <p className="text-foreground-500 text-xs md:text-sm">
                    {slug === 'all' ? '全部动漫' : currentCategory?.name} · {filteredList.length} 部作品
                  </p>
                  <div className="flex items-center gap-1 p-1 bg-background-800 rounded-full">
                    {[
                      { key: 'latest' as const, label: '最新' },
                      { key: 'rating' as const, label: '评分' },
                      { key: 'views' as const, label: '热度' },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setSortBy(opt.key)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                          sortBy === opt.key
                            ? 'bg-primary-500 text-white'
                            : 'text-foreground-400 hover:text-foreground-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {filteredList.map((anime) => (
                    <Link
                      key={anime.id}
                      to={`/article/${anime.id}`}
                      className="anime-card group"
                    >
                      <div className="aspect-[2/3] overflow-hidden rounded-lg bg-background-800 relative">
                        <img
                          src={anime.coverImage}
                          alt={anime.title}
                          title={`${anime.title} - 6080影视免费高清在线观看`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-accent-500/90 text-background-950 text-xs font-bold">
                          {anime.rating}
                        </div>
                        {anime.status === '更新中' && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-primary-500/90 text-white text-xs font-medium">
                            {anime.status}
                          </div>
                        )}
                      </div>
                      <div className="mt-2.5">
                        <h3 className="text-foreground-100 text-sm font-medium line-clamp-1 group-hover:text-primary-400 transition-colors">
                          {anime.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-foreground-500 text-xs">{anime.region}</span>
                          <span className="text-foreground-700 text-xs">·</span>
                          <span className="text-foreground-500 text-xs">更新至 {anime.episodes.length} 集</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {filteredList.length === 0 && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-800 rounded-full">
                      <i className="ri-emotion-sad-line text-3xl text-foreground-600"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无动漫，请选择其他分类</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
    </>
  );
}